import { useState } from "react";
import { Copy, Loader2, Mail, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export type LeadFollowup = {
  id: string;
  lead_table: string;
  lead_id: string;
  status: "ready" | "failed";
  subject: string | null;
  email_body: string | null;
  summary: string | null;
  recommended_offer: string | null;
  pillars: string[];
  error: string | null;
  updated_at: string;
};

type Props = {
  table: "masterclass_registrations" | "strategy_session_submissions";
  leadId: string;
  email: string;
  followup?: LeadFollowup;
  onChanged: () => void;
};

export function LeadFollowupPanel({ table, leadId, email, followup, onChanged }: Props) {
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    const { data, error } = await supabase.functions.invoke("lead-followups", {
      body: { lead_table: table, lead_id: leadId, force: true },
    });
    setBusy(false);
    if (error || data?.results?.[0]?.ok === false) {
      toast.error("Couldn't write the follow-up. Try again in a moment.");
    } else toast.success("Follow-up ready");
    onChanged();
  };

  const copy = async () => {
    if (!followup?.email_body) return;
    await navigator.clipboard.writeText(`Subject: ${followup.subject}\n\n${followup.email_body}`);
    toast.success("Copied");
  };

  const mailto = followup?.email_body
    ? `mailto:${email}?subject=${encodeURIComponent(followup.subject ?? "")}&body=${encodeURIComponent(followup.email_body)}`
    : undefined;

  return (
    <section className="mt-4 rounded-md border border-border bg-muted/40 p-4" aria-label="Coach follow-up">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <Sparkles className="h-4 w-4 text-primary" /> Coach follow-up
        </h3>
        <div className="flex flex-wrap gap-2">
          {followup?.status === "ready" && (
            <>
              <Button size="sm" variant="outline" onClick={copy}><Copy className="mr-1 h-3.5 w-3.5" />Copy</Button>
              <Button size="sm" variant="outline" asChild>
                <a href={mailto}><Mail className="mr-1 h-3.5 w-3.5" />Open in email</a>
              </Button>
            </>
          )}
          <Button size="sm" variant="secondary" onClick={run} disabled={busy}>
            {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
            {followup ? "Rewrite" : "Write follow-up"}
          </Button>
        </div>
      </div>
      {!followup && <p className="text-sm text-muted-foreground">Being written — refresh in a moment.</p>}
      {followup?.status === "failed" && (
        <p className="text-sm text-destructive">Last attempt failed. Click Rewrite to try again.</p>
      )}
      {followup?.status === "ready" && (
        <div className="space-y-3 text-sm">
          {followup.summary && <p className="text-muted-foreground">{followup.summary}</p>}
          <div className="flex flex-wrap gap-2">
            {followup.recommended_offer && <Badge>{followup.recommended_offer}</Badge>}
            {followup.pillars.map((p) => <Badge key={p} variant="outline">{p}</Badge>)}
          </div>
          <p className="font-medium">Subject: {followup.subject}</p>
          <p className="whitespace-pre-wrap">{followup.email_body}</p>
        </div>
      )}
    </section>
  );
}
